import { useState } from 'react';
import './FilterPanel.css';
import TextFilter from './TextFilter';
import AgeRangeFilter from './AgeRangeFilter';
import SelectFilter from './SelectFilter';
import CheckboxGroupFilter from './CheckboxGroupFilter';
import useAuthStore from '../stores/authStore';
import filterLimitLevel from '../config/filterLimitLevel';

// Helper function to check if filter is allowed
const isFilterAllowed = (requiredLevel, userLevel, isLoggedIn) => {
  if (!isLoggedIn) return requiredLevel === 0;
  return userLevel >= requiredLevel;
};

const FilterPanel = ({ onFilterChange, onToggleFilters }) => {
  const { isAuthenticated, user } = useAuthStore();
  const isLoggedIn = isAuthenticated();
  const userLevel = user?.level || 0;
  
  // Load filters from localStorage on mount
  const [filters, setFilters] = useState(() => {
    const savedFilters = localStorage.getItem('searchFilters');
    if (savedFilters) {
      try {
        return JSON.parse(savedFilters);
      } catch (error) {
        console.error('Failed to parse saved filters:', error);
      }
    }
    // Default empty filters
    return {
      searchName: '',
      isTechnical: '',
      ageMin: '',
      ageMax: '',
      isWoman: '',
      isYcFounder: '',
      searchLocation: '',
      timing: '',
      hasIdea: '',
      searchAboutMe: '',
      searchImpressiveThing: '',
      searchEducation: '',
      searchEmployment: '',
      hasCompany: '',
      hasCompanyUrl: '',
      searchCompany: '',
      searchCompanyDescription: '',
      searchCompanyProgress: '',
      searchFundingStatus: '',
      hasPotentialIdeas: '',
      searchPotentialIdeas: '',
      interests: [],
      responsibilities: [],
      hasCf: '',
      currentCfTechnical: '',
      cfIsTechnical: '',
      cfHasIdea: '',
      cfResponsibilities: [],
      cfLocation: '',
      cfAgeMin: '',
      cfAgeMax: '',
      cfIsFellowAlumniImportance: '',
      cfIsYcAlumImportance: '',
      cfIsWomanImportance: '',
      searchIdealCofounder: '',
      searchEquity: ''
    };
  });

  const [showAdvanced, setShowAdvanced] = useState(false);

  // Restore filter form values from localStorage, but do NOT auto-apply on mount.
  // Auto-apply was hiding most co-founders behind old saved filters (pagination stuck on page 1).
  
  const responsibilityOptions = [
    { label: 'Product', value: 'product' },
    { label: 'Engineering', value: 'engineering' },
    { label: 'Design', value: 'design' },
    { label: 'Sales and marketing', value: 'sales_marketing' },
    { label: 'Operations', value: 'operations' }
  ];

  const interestOptions = [
    { label: 'Agriculture / Agtech', value: 'agriculture' },
    { label: 'Artificial Intelligence', value: 'ai' },
    { label: 'Augmented Reality / Virtual Reality', value: 'arvr' },
    { label: 'B2B / Enterprise', value: 'b2b' },
    { label: 'Biomedical / Biotech', value: 'biotech' },
    { label: 'Blockchain', value: 'blockchain' },
    { label: 'Climate / Sustainability', value: 'climate' },
    { label: 'Consumer', value: 'consumer' },
    { label: 'E-Commerce', value: 'ecommerce' },
    { label: 'Developer Tools', value: 'devtools' },
    { label: 'Education / Edtech', value: 'edtech' },
    { label: 'Energy', value: 'energy' },
    { label: 'Entertainment', value: 'entertainment' },
    { label: 'Financial / Fintech', value: 'fintech' },
    { label: 'Food / Beverage', value: 'food' },
    { label: 'Gaming', value: 'gaming' },
    { label: 'Government', value: 'government' },
    { label: 'Hardware', value: 'hardware' },
    { label: 'Hard Tech', value: 'hard_tech' },
    { label: 'Health / Wellness', value: 'health' },
    { label: 'Healthcare', value: 'healthcare' },
    { label: 'Marketplace', value: 'marketplace' },
    { label: 'Non-Profit', value: 'nonprofit' },
    { label: 'Real Estate / Proptech', value: 'proptech' },
    { label: 'Robotics', value: 'robotics' },
    { label: 'Security', value: 'security' },
    { label: 'Travel / Tourism', value: 'travel' }
  ];

  const handleInputChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
  };

  const handleArrayChange = (field, value, checked) => {
    const currentArray = filters[field] || [];
    const newArray = checked 
      ? [...currentArray, value]
      : currentArray.filter(item => item !== value);
    
    const newFilters = { ...filters, [field]: newArray };
    setFilters(newFilters);
  };

  const applyFilters = () => {
    const cleanFilters = {};
    
    Object.keys(filters).forEach(key => {
      const value = filters[key];
      
      // Skip search fields if they have 1 or 2 characters
      if ((key === 'searchAboutMe' || key === 'searchImpressiveThing' || key === 'searchEducation' || key === 'searchEmployment' || key === 'searchCompanyDescription' || key === 'searchCompanyProgress' || key === 'searchFundingStatus' || key === 'searchPotentialIdeas' || key === 'searchIdealCofounder' || key === 'searchEquity') && value.length > 0 && value.length < 3) {
        return; // Skip this filter (1-2 chars not allowed)
      }
      
      if (value !== '' && value !== null && value !== undefined) {
        if (Array.isArray(value) && value.length > 0) {
          cleanFilters[key] = value;
        } else if (!Array.isArray(value)) {
          if (key === 'isWoman' || key === 'isTechnical' || key === 'isYcFounder' || key === 'hasCf' || key === 'currentCfTechnical' || key === 'cfIsTechnical' || key === 'cfHasIdea' || key === 'hasCompany' || key === 'hasCompanyUrl' || key === 'hasPotentialIdeas' || key === 'cfIsFellowAlumniImportance' || key === 'cfIsYcAlumImportance' || key === 'cfIsWomanImportance') {
            cleanFilters[key] = value === 'true';
          } else if (key === 'ageMin' || key === 'ageMax' || key === 'cfAgeMin' || key === 'cfAgeMax') {
            cleanFilters[key] = parseInt(value);
          } else {
            cleanFilters[key] = value;
          }
        }
      }
    });

    // Save filters to localStorage when applying
    localStorage.setItem('searchFilters', JSON.stringify(filters));

    onFilterChange(cleanFilters);
  };

  const clearFilters = () => {
    const emptyFilters = {
      searchName: '',
      searchAboutMe: '',
      searchImpressiveThing: '',
      searchEducation: '',
      searchEmployment: '',
      ageMin: '',
      ageMax: '',
      isWoman: '',
      isTechnical: '',
      isYcFounder: '',
      searchLocation: '',
      timing: '',
      interests: [],
      responsibilities: [],
      hasIdea: '',
      hasCf: '',
      currentCfTechnical: '',
      hasCompany: '',
      hasCompanyUrl: '',
      searchCompany: '',
      searchCompanyDescription: '',
      searchCompanyProgress: '',
      searchFundingStatus: '',
      hasPotentialIdeas: '',
      searchPotentialIdeas: '',
      cfIsTechnical: '',
      cfHasIdea: '',
      cfResponsibilities: [],
      cfLocation: '',
      cfAgeMin: '',
      cfAgeMax: '',
      cfIsFellowAlumniImportance: '',
      cfIsYcAlumImportance: '',
      cfIsWomanImportance: '',
      searchIdealCofounder: '',
      searchEquity: ''
    };
    setFilters(emptyFilters);
    
    // Clear filters from localStorage when clearing
    localStorage.removeItem('searchFilters');
    
    onFilterChange({});
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      applyFilters();
    }
  };

  return (
    <div className="filter-panel">
      <div className="filter-content">
        <div className="filter-header">
          <h3>Search Filters</h3>
          <button 
            className="filter-close-btn"
            onClick={onToggleFilters}
            title="Hide Filters"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 17L13 12L18 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M11 17L6 12L11 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <TextFilter
          label="Search by Name"
          placeholder="Search by name..."
          value={filters.searchName}
          onChange={(e) => handleInputChange('searchName', e.target.value)}
          onKeyDown={handleKeyPress}
          minLength={1}
          showHelperText={false}
        />

        <AgeRangeFilter
          label="Age Range"
          minValue={filters.ageMin}
          maxValue={filters.ageMax}
          onMinChange={(e) => handleInputChange('ageMin', e.target.value)}
          onMaxChange={(e) => handleInputChange('ageMax', e.target.value)}
          onKeyDown={handleKeyPress}
        />

        <SelectFilter
          label="Gender"
          value={filters.isWoman}
          onChange={(e) => handleInputChange('isWoman', e.target.value)}
          options={[
            { label: 'Any', value: '' },
            { label: 'Woman', value: 'true' },
            { label: 'Man', value: 'false' }
          ]}
        />

        <SelectFilter
          label="Technical Background"
          requiredLevel={filterLimitLevel.isTechnical}
          userLevel={userLevel}
          isLoggedIn={isLoggedIn}
          value={filters.isTechnical}
          onChange={(e) => handleInputChange('isTechnical', e.target.value)}
          disabled={!isFilterAllowed(filterLimitLevel.isTechnical, userLevel, isLoggedIn)}
          options={[
            { label: 'Any', value: '' },
            { label: 'Technical', value: 'true' },
            { label: 'Non-technical', value: 'false' }
          ]}
        />

        <TextFilter
          label="Location"
          requiredLevel={filterLimitLevel.location}
          userLevel={userLevel}
          isLoggedIn={isLoggedIn}
          placeholder="City, Region/States, Country, Continent"
          value={filters.searchLocation}
          onChange={(e) => handleInputChange('searchLocation', e.target.value)}
          onKeyDown={handleKeyPress}
          minLength={1}
          showHelperText={false}
          disabled={!isFilterAllowed(filterLimitLevel.location, userLevel, isLoggedIn)}
        />

        <SelectFilter
          label="Timing"
          requiredLevel={filterLimitLevel.timing}
          userLevel={userLevel}
          isLoggedIn={isLoggedIn}
          value={filters.timing}
          onChange={(e) => handleInputChange('timing', e.target.value)}
          disabled={!isFilterAllowed(filterLimitLevel.timing, userLevel, isLoggedIn)}
          options={[
            { label: 'Any', value: '' },
            { label: 'Active', value: 'Active' },
            { label: 'Ready', value: 'Ready' },
            { label: 'Year', value: 'Year' },
            { label: 'Passive', value: 'Passive' }
          ]}
        />
        
        <SelectFilter
          label="Has Business Idea"
          requiredLevel={filterLimitLevel.hasIdea}
          userLevel={userLevel}
          isLoggedIn={isLoggedIn}
          value={filters.hasIdea}
          onChange={(e) => handleInputChange('hasIdea', e.target.value)}
          disabled={!isFilterAllowed(filterLimitLevel.hasIdea, userLevel, isLoggedIn)}
          options={[
            { label: 'Any', value: '' },
            { label: 'Committed', value: 'committed' },
            { label: 'Open', value: 'open' },
            { label: 'None', value: 'none' }
          ]}
        />

        <button 
          className="toggle-advanced"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          {showAdvanced ? 'Hide' : 'Show'} Advanced Filters
        </button>

        {showAdvanced && (
          <div className="advanced-filters">
            <TextFilter
              label="Search About Me"
              requiredLevel={filterLimitLevel.searchAboutMe}
              userLevel={userLevel}
              isLoggedIn={isLoggedIn}
              placeholder="e.g., startup, founder"
              value={filters.searchAboutMe}
              onChange={(e) => handleInputChange('searchAboutMe', e.target.value)}
              onKeyDown={handleKeyPress}
              minLength={3}
              disabled={!isFilterAllowed(filterLimitLevel.searchAboutMe, userLevel, isLoggedIn)}
            />

            <TextFilter
              label="Search Impressive Accomplishment"
              requiredLevel={filterLimitLevel.searchImpressiveThing}
              userLevel={userLevel}
              isLoggedIn={isLoggedIn}
              placeholder="e.g., Award, Achievement"
              value={filters.searchImpressiveThing}
              onChange={(e) => handleInputChange('searchImpressiveThing', e.target.value)}
              onKeyDown={handleKeyPress}
              minLength={3}
              disabled={!isFilterAllowed(filterLimitLevel.searchImpressiveThing, userLevel, isLoggedIn)}
            />

            <TextFilter
              label="Search Education"
              requiredLevel={filterLimitLevel.searchEducation}
              userLevel={userLevel}
              isLoggedIn={isLoggedIn}
              placeholder="e.g., Bachelor, Computer Science"
              value={filters.searchEducation}
              onChange={(e) => handleInputChange('searchEducation', e.target.value)}
              onKeyDown={handleKeyPress}
              minLength={3}
              disabled={!isFilterAllowed(filterLimitLevel.searchEducation, userLevel, isLoggedIn)}
            />

            <TextFilter
              label="Search Employment"
              requiredLevel={filterLimitLevel.searchEmployment}
              userLevel={userLevel}
              isLoggedIn={isLoggedIn}
              placeholder="e.g., Google, Software Engineer"
              value={filters.searchEmployment}
              onChange={(e) => handleInputChange('searchEmployment', e.target.value)}
              onKeyDown={handleKeyPress}
              minLength={3}
              disabled={!isFilterAllowed(filterLimitLevel.searchEmployment, userLevel, isLoggedIn)}
            />

            <SelectFilter
              label="YC Founder"
              requiredLevel={filterLimitLevel.isYcFounder}
              userLevel={userLevel}
              isLoggedIn={isLoggedIn}
              value={filters.isYcFounder}
              onChange={(e) => handleInputChange('isYcFounder', e.target.value)}
              disabled={!isFilterAllowed(filterLimitLevel.isYcFounder, userLevel, isLoggedIn)}
              options={[
                { label: 'Any', value: '' },
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' }
              ]}
            />

            <SelectFilter
              label="Has Company"
              requiredLevel={filterLimitLevel.hasCompany}
              userLevel={userLevel}
              isLoggedIn={isLoggedIn}
              value={filters.hasCompany}
              onChange={(e) => handleInputChange('hasCompany', e.target.value)}
              disabled={!isFilterAllowed(filterLimitLevel.hasCompany, userLevel, isLoggedIn)}
              options={[
                { label: 'Any', value: '' },
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' }
              ]}
            />

            {filters.hasCompany === 'true' && (
              <>
                <SelectFilter
                  label="Has Company URL"
                  requiredLevel={filterLimitLevel.hasCompanyUrl}
                  userLevel={userLevel}
                  isLoggedIn={isLoggedIn}
                  value={filters.hasCompanyUrl}
                  onChange={(e) => handleInputChange('hasCompanyUrl', e.target.value)}
                  disabled={!isFilterAllowed(filterLimitLevel.hasCompanyUrl, userLevel, isLoggedIn)}
                  options={[
                    { label: 'Any', value: '' },
                    { label: 'Yes', value: 'true' },
                    { label: 'No', value: 'false' }
                  ]}
                />

                <TextFilter
                  label="Search Company Name"
                  requiredLevel={filterLimitLevel.searchCompany}
                  userLevel={userLevel}
                  isLoggedIn={isLoggedIn}
                  placeholder="Search by company name..."
                  value={filters.searchCompany}
                  onChange={(e) => handleInputChange('searchCompany', e.target.value)}
                  onKeyDown={handleKeyPress}
                  minLength={1}
                  showHelperText={false}
                  disabled={!isFilterAllowed(filterLimitLevel.searchCompany, userLevel, isLoggedIn)}
                />

                <TextFilter
                  label="Search Company Description"
                  requiredLevel={filterLimitLevel.searchCompanyDescription}
                  userLevel={userLevel}
                  isLoggedIn={isLoggedIn}
                  placeholder="e.g., AI, SaaS"
                  value={filters.searchCompanyDescription}
                  onChange={(e) => handleInputChange('searchCompanyDescription', e.target.value)}
                  onKeyDown={handleKeyPress}
                  minLength={3}
                  disabled={!isFilterAllowed(filterLimitLevel.searchCompanyDescription, userLevel, isLoggedIn)}
                />

                <TextFilter
                  label="Search Company Progress"
                  requiredLevel={filterLimitLevel.searchCompanyProgress}
                  userLevel={userLevel}
                  isLoggedIn={isLoggedIn}
                  placeholder="e.g., MVP, Beta"
                  value={filters.searchCompanyProgress}
                  onChange={(e) => handleInputChange('searchCompanyProgress', e.target.value)}
                  onKeyDown={handleKeyPress}
                  minLength={3}
                  disabled={!isFilterAllowed(filterLimitLevel.searchCompanyProgress, userLevel, isLoggedIn)}
                />

                <TextFilter
                  label="Search Funding Status"
                  requiredLevel={filterLimitLevel.searchFundingStatus}
                  userLevel={userLevel}
                  isLoggedIn={isLoggedIn}
                  placeholder="e.g., Bootstrapped, Seed"
                  value={filters.searchFundingStatus}
                  onChange={(e) => handleInputChange('searchFundingStatus', e.target.value)}
                  onKeyDown={handleKeyPress}
                  minLength={3}
                  disabled={!isFilterAllowed(filterLimitLevel.searchFundingStatus, userLevel, isLoggedIn)}
                />
              </>
            )}

            <SelectFilter
              label="Has Potential Ideas"
              requiredLevel={filterLimitLevel.hasPotentialIdeas}
              userLevel={userLevel}
              isLoggedIn={isLoggedIn}
              value={filters.hasPotentialIdeas}
              onChange={(e) => handleInputChange('hasPotentialIdeas', e.target.value)}
              disabled={!isFilterAllowed(filterLimitLevel.hasPotentialIdeas, userLevel, isLoggedIn)}
              options={[
                { label: 'Any', value: '' },
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' }
              ]}
            />

            {filters.hasPotentialIdeas === 'true' && (
              <TextFilter
                label="Search Potential Ideas"
                requiredLevel={filterLimitLevel.searchPotentialIdeas}
                userLevel={userLevel}
                isLoggedIn={isLoggedIn}
                placeholder="Search potential ideas..."
                value={filters.searchPotentialIdeas}
                onChange={(e) => handleInputChange('searchPotentialIdeas', e.target.value)}
                onKeyDown={handleKeyPress}
                minLength={3}
                disabled={!isFilterAllowed(filterLimitLevel.searchPotentialIdeas, userLevel, isLoggedIn)}
              />
            )}

            <CheckboxGroupFilter
              label="Interests"
              requiredLevel={filterLimitLevel.interests}
              userLevel={userLevel}
              isLoggedIn={isLoggedIn}
              options={interestOptions}
              selectedValues={filters.interests}
              onChange={(value, checked) => handleArrayChange('interests', value, checked)}
              disabled={!isFilterAllowed(filterLimitLevel.interests, userLevel, isLoggedIn)}
            />

            <CheckboxGroupFilter
              label="Responsibilities"
              requiredLevel={filterLimitLevel.responsibilities}
              userLevel={userLevel}
              isLoggedIn={isLoggedIn}
              options={responsibilityOptions}
              selectedValues={filters.responsibilities}
              onChange={(value, checked) => handleArrayChange('responsibilities', value, checked)}
              disabled={!isFilterAllowed(filterLimitLevel.responsibilities, userLevel, isLoggedIn)}
            />

            <CheckboxGroupFilter
              label="Preferred Co-founder Responsibilities"
              requiredLevel={filterLimitLevel.cfResponsibilities}
              userLevel={userLevel}
              isLoggedIn={isLoggedIn}
              options={responsibilityOptions}
              selectedValues={filters.cfResponsibilities}
              onChange={(value, checked) => handleArrayChange('cfResponsibilities', value, checked)}
              disabled={!isFilterAllowed(filterLimitLevel.cfResponsibilities, userLevel, isLoggedIn)}
            />

            <SelectFilter
              label="Already Has Co-founder"
              requiredLevel={filterLimitLevel.hasCf}
              userLevel={userLevel}
              isLoggedIn={isLoggedIn}
              value={filters.hasCf}
              onChange={(e) => handleInputChange('hasCf', e.target.value)}
              disabled={!isFilterAllowed(filterLimitLevel.hasCf, userLevel, isLoggedIn)}
              options={[
                { label: 'Any', value: '' },
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' }
              ]}
            />

            {filters.hasCf === 'true' && (
              <SelectFilter
                label="Has Technical Co-founder"
                value={filters.currentCfTechnical}
                onChange={(e) => handleInputChange('currentCfTechnical', e.target.value)}
                options={[
                  { label: 'Any', value: '' },
                  { label: 'Yes', value: 'true' },
                  { label: 'No', value: 'false' }
                ]}
              />
            )}

            <SelectFilter
              label="Wants Co-founder with Idea"
              requiredLevel={filterLimitLevel.cfHasIdea}
              userLevel={userLevel}
              isLoggedIn={isLoggedIn}
              value={filters.cfHasIdea}
              onChange={(e) => handleInputChange('cfHasIdea', e.target.value)}
              disabled={!isFilterAllowed(filterLimitLevel.cfHasIdea, userLevel, isLoggedIn)}
              options={[
                { label: 'Any', value: '' },
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' }
              ]}
            />

            <SelectFilter
              label="Seeking Technical Co-founder"
              requiredLevel={filterLimitLevel.cfIsTechnical}
              userLevel={userLevel}
              isLoggedIn={isLoggedIn}
              value={filters.cfIsTechnical}
              onChange={(e) => handleInputChange('cfIsTechnical', e.target.value)}
              disabled={!isFilterAllowed(filterLimitLevel.cfIsTechnical, userLevel, isLoggedIn)}
              options={[
                { label: 'Any', value: '' },
                { label: 'Yes', value: 'true' },
                { label: 'No', value: 'false' }
              ]}
            />

            <SelectFilter
              label="Preferred Co-founder Location"
              requiredLevel={filterLimitLevel.cfLocation}
              userLevel={userLevel}
              isLoggedIn={isLoggedIn}
              value={filters.cfLocation}
              onChange={(e) => handleInputChange('cfLocation', e.target.value)}
              disabled={!isFilterAllowed(filterLimitLevel.cfLocation, userLevel, isLoggedIn)}
              options={[
                { label: 'Any', value: '' },
                { label: 'Within', value: 'geographic' },
                { label: 'Country', value: 'country' },
                { label: 'Region', value: 'region' }
              ]}
            />

            <AgeRangeFilter
              label="Preferred Co-founder Age Range"
              requiredLevel={filterLimitLevel.cfAgeRange}
              userLevel={userLevel}
              isLoggedIn={isLoggedIn}
              minValue={filters.cfAgeMin}
              maxValue={filters.cfAgeMax}
              onMinChange={(e) => handleInputChange('cfAgeMin', e.target.value)}
              onMaxChange={(e) => handleInputChange('cfAgeMax', e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={!isFilterAllowed(filterLimitLevel.cfAgeRange, userLevel, isLoggedIn)}
            />

            <SelectFilter
              label="Wants Fellow Alumni Co-founder"
              requiredLevel={filterLimitLevel.cfIsFellowAlumniImportance}
              userLevel={userLevel}
              isLoggedIn={isLoggedIn}
              value={filters.cfIsFellowAlumniImportance}
              onChange={(e) => handleInputChange('cfIsFellowAlumniImportance', e.target.value)}
              disabled={!isFilterAllowed(filterLimitLevel.cfIsFellowAlumniImportance, userLevel, isLoggedIn)}
              options={[
                { label: 'Any', value: '' },
                { label: 'Prefer', value: 'true' },
                { label: 'Not important', value: 'false' }
              ]}
            />

            <SelectFilter
              label="Wants YC Alumni Co-founder"
              requiredLevel={filterLimitLevel.cfIsYcAlumImportance}
              userLevel={userLevel}
              isLoggedIn={isLoggedIn}
              value={filters.cfIsYcAlumImportance}
              onChange={(e) => handleInputChange('cfIsYcAlumImportance', e.target.value)}
              disabled={!isFilterAllowed(filterLimitLevel.cfIsYcAlumImportance, userLevel, isLoggedIn)}
              options={[
                { label: 'Any', value: '' },
                { label: 'Prefer', value: 'true' },
                { label: 'Not important', value: 'false' }
              ]}
            />

            <SelectFilter
              label="Wants Woman Co-founder"
              requiredLevel={filterLimitLevel.cfIsWomanImportance}
              userLevel={userLevel}
              isLoggedIn={isLoggedIn}
              value={filters.cfIsWomanImportance}
              onChange={(e) => handleInputChange('cfIsWomanImportance', e.target.value)}
              disabled={!isFilterAllowed(filterLimitLevel.cfIsWomanImportance, userLevel, isLoggedIn)}
              options={[
                { label: 'Any', value: '' },
                { label: 'Prefer', value: 'true' },
                { label: 'Not important', value: 'false' }
              ]}
            />

            <TextFilter
              label="Search Ideal Co-founder"
              requiredLevel={filterLimitLevel.searchIdealCofounder}
              userLevel={userLevel}
              isLoggedIn={isLoggedIn}
              placeholder="Search ideal co-founder description..."
              value={filters.searchIdealCofounder}
              onChange={(e) => handleInputChange('searchIdealCofounder', e.target.value)}
              onKeyDown={handleKeyPress}
              minLength={3}
              disabled={!isFilterAllowed(filterLimitLevel.searchIdealCofounder, userLevel, isLoggedIn)}
            />

            <TextFilter
              label="Search Equity"
              requiredLevel={filterLimitLevel.searchEquity}
              userLevel={userLevel}
              isLoggedIn={isLoggedIn}
              placeholder="Search equity information..."
              value={filters.searchEquity}
              onChange={(e) => handleInputChange('searchEquity', e.target.value)}
              onKeyDown={handleKeyPress}
              minLength={3}
              disabled={!isFilterAllowed(filterLimitLevel.searchEquity, userLevel, isLoggedIn)}
            />
          </div>
        )}
      </div>

      <div className="filter-actions">
        <button className="apply-btn" onClick={applyFilters}>
          Apply
        </button>
        <button className="clear-btn" onClick={clearFilters}>
          Clear All
        </button>
      </div>
    </div>
  );
};

export default FilterPanel;


